import React, { useEffect, useState } from 'react'

import useDialog from '../../../hooks/dialog/useDialog'
import useInstantOnboarding from '../../../hooks/useInstantOnboarding'
import RelayPickerDialog from './RelayPickerDialog'
import { defaultRelayHost, fetchRelayDirectory } from './relayDirectory'
import styles from './styles.module.scss'

// English is hardcoded here (like the rest of patch desktop/0042) so the relay
// picker stays a self-contained patch and doesn't have to touch every locale
// file. See PATCHES.md.

/**
 * slothfulchat-web: on the instant-onboarding ("create profile") screen, a row
 * showing which public chatmail relay the new address will be created on, with
 * a button that opens the relay picker dialog (RelayPickerDialog) to change it.
 *
 * The list comes live from the chatmail relay directory (relayDirectory.ts);
 * fetching it is cheap and done here on mount, but probing each relay over the
 * WS→TCP bridge is not, so that happens in the dialog, once opened. The row
 * always reflects the chosen relay (kept in InstantOnboardingContext), so the
 * consent link and account creation can't disagree with what's shown.
 *
 * Renders nothing while the directory is loading, when it is unavailable, when
 * there is nothing to choose (fewer than two relays), or when a scanned
 * `dcaccount:`/`dclogin:` QR already pins the server.
 */
export default function RelayPicker() {
  const { welcomeQr, selectedRelayHost, setSelectedRelayHost } =
    useInstantOnboarding()
  const { openDialog } = useDialog()
  const [relays, setRelays] = useState<string[] | null>(null)

  const defaultHost = defaultRelayHost()

  // With a scanned account/login QR the server is fixed — no choice to offer.
  const relayChoosable =
    welcomeQr?.qr.kind !== 'account' && welcomeQr?.qr.kind !== 'login'

  useEffect(() => {
    if (!relayChoosable) {
      return
    }
    let stale = false
    fetchRelayDirectory().then(list => {
      if (!stale) {
        setRelays(list)
      }
    })
    return () => {
      stale = true
    }
  }, [relayChoosable])

  if (!relayChoosable || relays === null || relays.length < 2) {
    return null
  }

  // The chosen relay may be a directory relay, the default, or a custom host
  // typed earlier this session (kept in context, so not in `relays`).
  const current =
    selectedRelayHost && selectedRelayHost !== defaultHost
      ? selectedRelayHost
      : defaultHost

  const openPicker = () =>
    openDialog(RelayPickerDialog, {
      relays,
      defaultHost,
      selectedHost: current,
      onSelect: (host?: string) => setSelectedRelayHost(host),
    })

  return (
    <div className={styles.relayPicker}>
      <span className={styles.relayPickerLabel}>
        Chatmail relay for your new address
      </span>
      <button
        type='button'
        data-testid='relay-picker-button'
        className={styles.relayPickerTrigger}
        onClick={openPicker}
      >
        <span className={styles.relayPickerTriggerValue}>
          {current}
          {current === defaultHost && (
            <span className={styles.relayPickerDefaultTag}>default</span>
          )}
        </span>
        <span className={styles.relayPickerTriggerAction}>Change</span>
      </button>
    </div>
  )
}
